// A job's Job value, Variations and Invoicing tabs (round 45).
//
// Nathan: "look at Zanetto Tools jobs and look at the Job Value tab ... I
// want to implement this identically, pixel by pixel and function by
// function ... variations and invoicing too". So the markup is ZT's
// (JobDetail.jsx's blue financial cards, its InfoField grid, its variation
// table and invoice list, its three dialogs), with Dayboard's rules applied
// where the two differ:
//
//   * figures are integer cents from the server, never floats added up here;
//   * dates read DD/MM/YYYY, and nothing customers read carries an em dash;
//   * every file picker takes a drop (FileDrop), and an invoice's document
//     opens in the app's viewer rather than as a bare link;
//   * destructive actions confirm through the app's own dialog.
//
// Manager+ only: the page draws these tabs for nobody else, and the server's
// jobs router refuses everyone else before any of this is read.
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { ApiError } from '../../api';
import { formatDate, formatMomentAu, todayISO } from '../../lib/dates';
import { formatCents } from '../../quotesApi';
import { jobMoneyApi, type JobInvoice, type JobMoneyBlock, type Variation, type VariationStatus } from '../../jobMoneyApi';
import { Banner } from '../../ui';
import { Modal } from '../ui/Modal';
import { useConfirm } from '../ui/confirm';
import { StatusBadge } from '../ui/StatusBadge';
import { SingleFileDrop } from '../ui/FileDrop';
import { DocumentViewer } from '../DocumentViewer';
import type { Attachment } from '../../documentsApi';

const JobCashflowChart = lazy(() => import('./JobCashflowChart'));

/** Cents back to the plain text a money box holds: 125050 -> "1250.50". */
function centsToInput(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const magnitude = Math.abs(cents);
  return `${sign}${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`;
}

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// --- ZT's card chrome ----------------------------------------------------------

/** ZT's blue financial card header: accent bar, uppercase title, a pill. */
function CardHeader({ title, pill, children }: { title: string; pill: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-b border-blue-200 bg-blue-50/60 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-4 w-1 rounded-full bg-blue-500" aria-hidden="true" />
          <h2 className="!mb-0 text-xs font-semibold uppercase tracking-wider text-blue-700">{title}</h2>
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
            {pill}
          </span>
        </div>
      </div>
      {children}
    </div>
  );
}

function InfoField({ label, value, action }: { label: string; value: ReactNode; action?: ReactNode }) {
  return (
    <div>
      <div className="mb-0.5 flex items-center gap-1">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
        {action}
      </div>
      <div className="text-sm tabular-nums text-gray-900">{value}</div>
    </div>
  );
}

const PlusIcon = () => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

// --- Job value -----------------------------------------------------------------

export function JobValuePanel({
  jobId,
  block,
  isAdmin,
  onChanged,
}: {
  jobId: string;
  block: JobMoneyBlock;
  isAdmin: boolean;
  onChanged: (next: JobMoneyBlock) => void;
}) {
  const { money } = block;
  const [editing, setEditing] = useState(false);

  return (
    <>
      <div className="card mb-4 border-blue-200">
        <CardHeader title="Job Value" pill="Financials" />
        <p className="px-5 pt-3 text-xs text-gray-500">Values include GST.</p>
        <div className="grid grid-cols-2 gap-4 px-5 py-4 sm:grid-cols-3">
          <InfoField
            label="Original Value"
            value={formatCents(money.originalValueCents)}
            action={
              isAdmin && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  title="Edit Original Value"
                  aria-label="Edit Original Value"
                  className="btn-icon h-8 w-8"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              )
            }
          />
          <InfoField label="Approved Variations" value={formatCents(money.approvedVariationsCents)} />
          <InfoField label="Current Value" value={<span className="font-bold">{formatCents(money.currentValueCents)}</span>} />
          <InfoField label="Invoiced" value={formatCents(money.invoicedCents)} />
          <InfoField
            label="Outstanding Balance"
            value={
              <span className={`font-bold ${money.outstandingCents < 0 ? 'text-red-600' : ''}`}>
                {formatCents(money.outstandingCents)}
              </span>
            }
          />
        </div>
      </div>

      <div className="card mb-4 border-blue-200">
        <CardHeader title="Cashflow" pill="Financials" />
        <Suspense fallback={<div className="px-5 py-8 text-center text-sm text-gray-500">Loading the chart…</div>}>
          <JobCashflowChart invoices={block.invoices} />
        </Suspense>
      </div>

      {editing && (
        <OriginalValueModal
          jobId={jobId}
          current={money.originalValueCents}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            onChanged(next);
          }}
        />
      )}
    </>
  );
}

function OriginalValueModal({
  jobId,
  current,
  onClose,
  onSaved,
}: {
  jobId: string;
  current: number;
  onClose: () => void;
  onSaved: (next: JobMoneyBlock) => void;
}) {
  const [value, setValue] = useState(centsToInput(current));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await jobMoneyApi.setOriginalValue(jobId, value));
    } catch (err) {
      setError(message(err, 'Could not save the value.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Edit Original Value"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="button" onClick={() => void save()} disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <Banner kind="error">{error}</Banner>}
      <label className="form-label" htmlFor="original-value">
        Original Value ($, incl. GST)
      </label>
      <input
        id="original-value"
        className="form-input text-right tabular-nums"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
      <p className="mt-1 text-xs text-gray-500">
        Current value will be recalculated from this plus any approved variations.
      </p>
    </Modal>
  );
}

// --- Variations ------------------------------------------------------------------

export function VariationsPanel({
  jobId,
  block,
  canAdd,
  onChanged,
}: {
  jobId: string;
  block: JobMoneyBlock;
  /** ZT's canAddVariation: anything but a cancelled job. */
  canAdd: boolean;
  onChanged: (next: JobMoneyBlock) => void;
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<{ mode: 'add' } | { mode: 'edit'; variation: Variation } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function setStatus(v: Variation, status: VariationStatus) {
    setError(null);
    try {
      onChanged(await jobMoneyApi.updateVariation(jobId, v.id, { status }));
    } catch (err) {
      setError(message(err, 'Could not change that variation.'));
    }
  }

  async function remove(v: Variation) {
    const ok = await confirm({
      title: 'Delete this variation?',
      message: `"${v.description}" will be removed${v.status === 'approved' ? ', and the job value goes down with it' : ''}. This cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      onChanged(await jobMoneyApi.deleteVariation(jobId, v.id));
    } catch (err) {
      setError(message(err, 'Could not delete that variation.'));
    }
  }

  function actions(v: Variation, phone: boolean) {
    return (
      <div className={`flex flex-wrap items-center justify-end ${phone ? 'mt-2 gap-2' : 'gap-1.5 whitespace-nowrap'}`}>
        {v.status === 'pending' && (
          <>
            <button type="button" onClick={() => void setStatus(v, 'approved')} className="row-action-success">
              Approve
            </button>
            <button type="button" onClick={() => void setStatus(v, 'rejected')} className="row-action-warning">
              Reject
            </button>
            <button type="button" onClick={() => setModal({ mode: 'edit', variation: v })} className="row-action">
              Edit
            </button>
          </>
        )}
        {(v.status === 'approved' || v.status === 'rejected') && (
          <button type="button" onClick={() => void setStatus(v, 'pending')} className="row-action-muted">
            Revert
          </button>
        )}
        {!phone && <span className="mx-0.5 h-4 w-px bg-gray-200" aria-hidden="true" />}
        <button type="button" onClick={() => void remove(v)} className="row-action-danger">
          Delete
        </button>
      </div>
    );
  }

  const variations = block.variations;

  return (
    <div className="card mb-4 border-blue-200">
      <CardHeader title="Variations" pill="Affects job value">
        {canAdd && (
          <button type="button" onClick={() => setModal({ mode: 'add' })} className="btn-secondary btn-sm flex-none">
            <PlusIcon />
            Add Variation
          </button>
        )}
      </CardHeader>
      {error && (
        <div className="px-5 pt-3">
          <Banner kind="error">{error}</Banner>
        </div>
      )}

      {variations.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-500">
          No variations yet.{' '}
          {canAdd && (
            <button type="button" onClick={() => setModal({ mode: 'add' })} className="row-action">
              Add Variation
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Phones: stacked variation cards with the actions underneath. */}
          <ul className="m-0 list-none divide-y divide-gray-100 p-0 sm:hidden">
            {variations.map((v) => (
              <li key={v.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-900">{v.description}</div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      Requested {formatDate(v.requestedDate)}
                      {v.approvedDate ? ` · approved ${formatDate(v.approvedDate)}` : ''}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">{v.createdByName}</div>
                  </div>
                  <div className="flex-none space-y-1 text-right">
                    <div className={`text-sm font-medium tabular-nums ${v.valueCents < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatCents(v.valueCents)}
                    </div>
                    <StatusBadge status={v.status} kind="variation" />
                  </div>
                </div>
                {actions(v, true)}
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto sm:block">
            <table className="data-table min-w-[640px]">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-right">Value (incl. GST)</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Approved</th>
                  <th>Added by</th>
                  <th className="w-44">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {variations.map((v) => (
                  <tr key={v.id}>
                    <td>{v.description}</td>
                    <td className={`text-right font-medium tabular-nums ${v.valueCents < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatCents(v.valueCents)}
                    </td>
                    <td>
                      <StatusBadge status={v.status} kind="variation" />
                    </td>
                    <td className="whitespace-nowrap text-xs text-gray-600">{formatDate(v.requestedDate)}</td>
                    <td className="whitespace-nowrap text-xs text-gray-600">{v.approvedDate ? formatDate(v.approvedDate) : '—'}</td>
                    <td className="text-xs text-gray-600">
                      {v.createdByName}
                      <div className="text-gray-500">{formatMomentAu(v.createdAt)}</div>
                    </td>
                    <td>{actions(v, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modal && (
        <VariationModal
          jobId={jobId}
          variation={modal.mode === 'edit' ? modal.variation : null}
          onClose={() => setModal(null)}
          onSaved={(next) => {
            setModal(null);
            onChanged(next);
          }}
        />
      )}
    </div>
  );
}

function VariationModal({
  jobId,
  variation,
  onClose,
  onSaved,
}: {
  jobId: string;
  variation: Variation | null;
  onClose: () => void;
  onSaved: (next: JobMoneyBlock) => void;
}) {
  const [description, setDescription] = useState(variation?.description ?? '');
  const [value, setValue] = useState(variation ? centsToInput(variation.valueCents) : '');
  const [requestedDate, setRequestedDate] = useState(variation?.requestedDate ?? todayISO());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        variation
          ? await jobMoneyApi.updateVariation(jobId, variation.id, { description, value, requestedDate })
          : await jobMoneyApi.addVariation(jobId, { description, value, requestedDate })
      );
    } catch (err) {
      setError(message(err, 'Could not save that variation.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={variation ? 'Edit Variation' : 'Add Variation'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="button" onClick={() => void save()} disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Saving…' : variation ? 'Save Changes' : 'Add Variation'}
          </button>
        </>
      }
    >
      {error && <Banner kind="error">{error}</Banner>}
      <div className="space-y-3">
        <div>
          <label className="form-label" htmlFor="variation-description">
            Description <span className="text-red-700">*</span>
          </label>
          <input
            id="variation-description"
            className="form-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="form-label" htmlFor="variation-value">
              Value ($, incl. GST) <span className="text-red-700">*</span>
            </label>
            <input
              id="variation-value"
              className="form-input text-right tabular-nums"
              inputMode="decimal"
              placeholder="0.00"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">Use a negative number for deductions.</p>
          </div>
          <div>
            <label className="form-label" htmlFor="variation-requested">
              Requested Date
            </label>
            <input
              id="variation-requested"
              type="date"
              className="form-input"
              value={requestedDate}
              onChange={(e) => setRequestedDate(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// --- Invoicing -------------------------------------------------------------------

export function InvoicingPanel({
  jobId,
  block,
  isAdmin,
  onChanged,
}: {
  jobId: string;
  block: JobMoneyBlock;
  isAdmin: boolean;
  onChanged: (next: JobMoneyBlock) => void;
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<{ mode: 'add' } | { mode: 'edit'; invoice: JobInvoice } | null>(null);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invoices = block.invoices;

  async function remove(inv: JobInvoice) {
    const ok = await confirm({
      title: `Delete invoice ${inv.invoiceNumber}?`,
      message:
        'The record and its attached document are removed, and the invoiced total goes down with it. This cannot be undone.',
      confirmLabel: 'Delete invoice',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      onChanged(await jobMoneyApi.deleteInvoice(jobId, inv.id));
    } catch (err) {
      setError(message(err, 'Could not delete that invoice.'));
    }
  }

  return (
    <div className="card mb-4 border-blue-200">
      <div className="flex flex-col gap-3 border-b border-blue-200 bg-blue-50/60 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-blue-500" aria-hidden="true" />
            <h2 className="!mb-0 text-xs font-semibold uppercase tracking-wider text-blue-700">Invoicing</h2>
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
              Financials
            </span>
          </div>
          <p className="!mb-0 mt-1 text-xs text-gray-600">
            Invoices raised for this job. Add as many as needed. Amounts are GST-inclusive.
          </p>
        </div>
        <button type="button" onClick={() => setModal({ mode: 'add' })} className="btn-secondary btn-sm flex-none">
          Add Invoice
        </button>
      </div>
      {error && (
        <div className="px-5 pt-3">
          <Banner kind="error">{error}</Banner>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-500">
          No invoices recorded yet.{' '}
          <button type="button" onClick={() => setModal({ mode: 'add' })} className="row-action">
            Add Invoice
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              {invoices.length} invoice{invoices.length > 1 ? 's' : ''}
            </span>
            <span className="text-sm font-bold tabular-nums text-gray-900">
              Total Invoiced (incl. GST): {formatCents(block.money.invoicedCents)}
            </span>
          </div>
          <ul className="m-0 list-none divide-y divide-gray-100 p-0">
            {invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</span>
                    <span className="text-xs text-gray-500">{formatDate(inv.invoicedDate)}</span>
                    {inv.amountCents !== null && (
                      <span className="text-sm font-semibold tabular-nums text-gray-900">
                        {formatCents(inv.amountCents)} <span className="text-xs font-normal text-gray-500">incl. GST</span>
                      </span>
                    )}
                  </div>
                  {inv.file ? (
                    <div className="mt-1 truncate text-xs text-gray-500">
                      <button type="button" className="table-link" onClick={() => setViewing(inv.file)}>
                        {inv.file.title}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-gray-500">No document attached</div>
                  )}
                  <div className="mt-0.5 text-xs text-gray-500">
                    {inv.createdByName} · {formatMomentAu(inv.createdAt)}
                  </div>
                </div>
                <div className="flex flex-none items-center gap-3">
                  <button type="button" onClick={() => setModal({ mode: 'edit', invoice: inv })} className="row-action">
                    Edit
                  </button>
                  {isAdmin && (
                    <button type="button" onClick={() => void remove(inv)} className="row-action-danger">
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {modal && (
        <InvoiceModal
          jobId={jobId}
          invoice={modal.mode === 'edit' ? modal.invoice : null}
          onClose={() => setModal(null)}
          onSaved={(next) => {
            setModal(null);
            onChanged(next);
          }}
        />
      )}
      {viewing && <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function InvoiceModal({
  jobId,
  invoice,
  onClose,
  onSaved,
}: {
  jobId: string;
  invoice: JobInvoice | null;
  onClose: () => void;
  onSaved: (next: JobMoneyBlock) => void;
}) {
  const [invoiceNumber, setInvoiceNumber] = useState(invoice?.invoiceNumber ?? '');
  const [invoicedDate, setInvoicedDate] = useState(invoice?.invoicedDate ?? todayISO());
  const [amount, setAmount] = useState(invoice && invoice.amountCents !== null ? centsToInput(invoice.amountCents) : '');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        invoice
          ? await jobMoneyApi.updateInvoice(jobId, invoice.id, { invoiceNumber, invoicedDate, amount }, file)
          : await jobMoneyApi.addInvoice(jobId, { invoiceNumber, invoicedDate, amount }, file)
      );
    } catch (err) {
      setError(message(err, 'Could not save that invoice.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={invoice ? 'Edit Invoice' : 'Add Invoice'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="button" onClick={() => void save()} disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Saving…' : invoice ? 'Save Changes' : 'Add Invoice'}
          </button>
        </>
      }
    >
      {error && <Banner kind="error">{error}</Banner>}
      <div className="space-y-3">
        <div>
          <label className="form-label" htmlFor="invoice-number">
            Invoice Number <span className="text-red-700">*</span>
          </label>
          <input
            id="invoice-number"
            className="form-input"
            placeholder="e.g. INV-1042"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="form-label" htmlFor="invoice-date">
              Invoiced Date
            </label>
            <input
              id="invoice-date"
              type="date"
              className="form-input"
              value={invoicedDate}
              onChange={(e) => setInvoicedDate(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label" htmlFor="invoice-amount">
              Amount ($, incl. GST){' '}
              {invoice ? (
                <span className="font-normal text-gray-500">(optional)</span>
              ) : (
                <span className="text-red-700">*</span>
              )}
            </label>
            <input
              id="invoice-amount"
              className="form-input text-right tabular-nums"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>
        <div>
          <span className="form-label">
            {invoice?.file ? 'Replace Attached Document' : 'Attach Invoice'}{' '}
            <span className="font-normal text-gray-500">(optional)</span>
          </span>
          <SingleFileDrop
            id="invoice-file"
            file={file}
            onChange={setFile}
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif"
            typeLabel="PDF or an image"
            onReject={setError}
          />
          <p className="mt-1 text-xs text-gray-500">
            {invoice?.file && !file
              ? `Current file: ${invoice.file.title}. Drop or choose a new file to replace it.`
              : 'A PDF or an image of the invoice.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}
